/**
 * Phase 21.4 Batch 3F — Admin manual Hall of Champions backfill modal.
 *
 * Purpose: Add historical winners (Weekly Spotlight, In The Booth,
 * Beat of the Week, Founder picks, past Contest winners) that were
 * awarded BEFORE the automated Contest Center existed. This is
 * historical bookkeeping — NO coin grants, NO Stripe / paid_balance
 * writes. Admin explicitly chooses reward path separately.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import ArtistPicker from "@/components/admin/ArtistPicker";
import BannerUploadField from "@/components/admin/BannerUploadField";
import DateTimeField from "@/components/admin/DateTimeField";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "weekly_spotlight", label: "🌟 Weekly Spotlight" },
  { value: "in_the_booth", label: "🎤 In The Booth" },
  { value: "beat_of_the_week", label: "🎧 Beat of the Week" },
  { value: "contest", label: "🏆 Contest / Challenge" },
  { value: "founder_pick", label: "👑 Founder Pick" },
  { value: "other", label: "Other" },
];

const emptyForm = {
  user_id: "",
  title: "",
  category: "weekly_spotlight",
  placement: 1,
  award_label: "",
  entry_type: "track",
  entry_ref_id: "",
  entry_title: "",
  entry_cover: "",
  badge_ids: "",   // comma-separated for form UX
  awarded_at: "",
  banner_image: "",
  notes: "",
  contest_id: "",
  contest_title: "",
  contest_slug: "",
};

export const HoFBackfillModal = ({ open, onClose, onCreated, initial = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && initial) {
      setForm({
        ...emptyForm,
        ...initial,
        badge_ids: (initial.badge_ids || []).join(", "),
      });
    } else if (open) {
      setForm(emptyForm);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.user_id || !form.title) {
      toast.error("Artist and Title are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id.trim(),
        title: form.title.trim(),
        category: form.category || null,
        placement: Number(form.placement) || 1,
        award_label: form.award_label || null,
        entry_type: form.entry_type || null,
        entry_ref_id: form.entry_ref_id || null,
        entry_title: form.entry_title || null,
        entry_cover: form.entry_cover || null,
        badge_ids: form.badge_ids
          ? form.badge_ids.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        awarded_at: form.awarded_at || null,
        banner_image: form.banner_image || null,
        notes: form.notes || null,
        contest_id: form.contest_id || null,
        contest_title: form.contest_title || form.title,
        contest_slug: form.contest_slug || null,
      };
      let r;
      if (initial?.hof_id) {
        r = await api.patch(`/admin/hall-of-champions/${initial.hof_id}`, payload);
        toast.success("Updated.");
      } else {
        r = await api.post("/admin/hall-of-champions", payload);
        toast.success("Added to Hall of Champions.");
      }
      onCreated?.(r.data.champion);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      onClick={onClose}
      data-testid="hof-backfill-modal"
    >
      <Card
        className="max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-[#0f0f14] border border-white/10 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-black text-white">🏛 {initial?.hof_id ? "Edit" : "Add"} Historical Winner</h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-xl leading-none"
            aria-label="Close"
            data-testid="hof-backfill-close"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <ArtistPicker
              value={form.user_id}
              onChange={({ user_id }) => set("user_id", user_id)}
              testIdPrefix="hof-backfill-artist-picker"
              label="Artist"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Award / Category
            </label>
            <select
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hof-backfill-category"
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value} className="bg-[#13131a]">{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Placement <span className="text-rose-400">*</span>
            </label>
            <select
              value={form.placement}
              onChange={(e) => set("placement", Number(e.target.value))}
              className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hof-backfill-placement"
            >
              <option value={1} className="bg-[#13131a]">🥇 1st Place / Winner</option>
              <option value={2} className="bg-[#13131a]">🥈 2nd Place / Runner-Up</option>
              <option value={3} className="bg-[#13131a]">🥉 3rd Place</option>
              <option value={4} className="bg-[#13131a]">#4</option>
              <option value={5} className="bg-[#13131a]">#5</option>
              <option value={6} className="bg-[#13131a]">#6+</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Title / Award description <span className="text-rose-400">*</span>
            </label>
            <Input
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. 'Weekly Spotlight — Week 12, 2025'"
              className="bg-white/5 border-white/10 text-white"
              data-testid="hof-backfill-title"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Entry type
            </label>
            <select
              value={form.entry_type}
              onChange={(e) => set("entry_type", e.target.value)}
              className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
              data-testid="hof-backfill-entry-type"
            >
              <option value="track" className="bg-[#13131a]">🎤 Track</option>
              <option value="beat" className="bg-[#13131a]">🎧 Beat</option>
              <option value="other" className="bg-[#13131a]">Other / n/a</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Entry ref_id (optional)
            </label>
            <Input
              value={form.entry_ref_id}
              onChange={(e) => set("entry_ref_id", e.target.value)}
              placeholder="submission_xxx or beat_xxx"
              className="bg-white/5 border-white/10 text-white"
              data-testid="hof-backfill-entry-ref-id"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Entry title
            </label>
            <Input
              value={form.entry_title}
              onChange={(e) => set("entry_title", e.target.value)}
              placeholder="e.g. 'Sun Rays'"
              className="bg-white/5 border-white/10 text-white"
              data-testid="hof-backfill-entry-title"
            />
          </div>

          <div className="sm:col-span-2">
            <BannerUploadField
              value={form.entry_cover}
              onChange={(url) => set("entry_cover", url)}
              purpose="cover"
              testIdPrefix="hof-backfill-entry-cover"
              label="Entry cover / artwork (optional)"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Badge IDs (comma-sep, optional)
            </label>
            <Input
              value={form.badge_ids}
              onChange={(e) => set("badge_ids", e.target.value)}
              placeholder="badge_xxx, badge_yyy"
              className="bg-white/5 border-white/10 text-white"
              data-testid="hof-backfill-badge-ids"
            />
          </div>

          <div>
            <DateTimeField
              value={form.awarded_at}
              onChange={(iso) => set("awarded_at", iso)}
              testId="hof-backfill-awarded-at"
              label="Awarded at"
              hint="Defaults to now if left empty."
            />
          </div>

          <div className="sm:col-span-2">
            <BannerUploadField
              value={form.banner_image}
              onChange={(url) => set("banner_image", url)}
              purpose="banner"
              testIdPrefix="hof-backfill-banner"
              label="Banner image (optional)"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-black block mb-1">
              Notes (optional)
            </label>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Any context to remember about this award."
              className="bg-white/5 border-white/10 text-white min-h-[60px]"
              data-testid="hof-backfill-notes"
              maxLength={800}
            />
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-100" data-testid="hof-backfill-safety-note">
          <strong>Historical bookkeeping only.</strong> Adding a row here does <em>not</em> grant coins, promo plays, or touch Stripe / payouts. Award rewards separately through the reward tools if desired.
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="hof-backfill-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !form.user_id || !form.title}
            className="bg-white text-black hover:bg-amber-200 font-black disabled:opacity-40"
            data-testid="hof-backfill-submit"
          >
            {saving ? "Saving…" : (initial?.hof_id ? "Save changes" : "Add to Hall of Champions")}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default HoFBackfillModal;
